import { CreateDonationRequestModeEnum, ProjectSettingsPaymentGatewayEnum } from '@/app/api';
import ButtonRadio from '@/app/components/ButtonRadio/ButtonRadio';
import { ApplePayLogo } from '@/app/components/PaymentMethods/Icons/ApplePayLogo';
import { GooglePayLogo } from '@/app/components/PaymentMethods/Icons/GooglePayLogo';
import { MastercardLogo } from '@/app/components/PaymentMethods/Icons/MastercardLogo';
import { VisaLogo } from '@/app/components/PaymentMethods/Icons/VisaLogo';
import { WireTransferIcon } from '@/app/components/PaymentMethods/Icons/WireTransferIcon';
import QRCodeIcon from '@/app/components/QRCodeIcon/QRCodeIcon';
import { PaymentMethod } from '@/app/enums';
import { PaymentMethodFormType, usePaymentMethods } from '@/app/hooks/usePaymentMethods';
import { useConfigContext } from '@/app/providers/ConfigProvider';
import { useProjectData } from '@/app/providers/ProjectProvider';
import classNames from 'classnames';
import { ErrorMessage, useField } from 'formik';
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

const paymentMethodsWithNote = [PaymentMethod.WireTransfer, PaymentMethod.QrCode];

export const resolveIsApplePaymentMethodSupported = () => {
  return false;
  // try {
  //   return window.ApplePaySession && window.ApplePaySession.canMakePayments();
  // } catch (error) {
  //   return false;
  // }
};

const PaymentMethods = ({
  donationMode,
  formType,
}: {
  formType: PaymentMethodFormType;
  donationMode?: CreateDonationRequestModeEnum | null;
}) => {
  const paymentMethods = usePaymentMethods(formType, donationMode);
  const { project } = useProjectData();
  const { isAppleDevice } = useConfigContext();

  const isTrustPayUsedInProject = project.settings.paymentGateway === ProjectSettingsPaymentGatewayEnum.Trustpay;
  const [hasHydrated, setHasHydrated] = useState(false);

  useEffect(() => {
    setHasHydrated(true);
  }, []);

  const { t } = useTranslation();
  const isApplePaySupported = useMemo(
    () =>
      paymentMethods.includes(PaymentMethod.ApplePay) &&
      resolveIsApplePaymentMethodSupported() &&
      isTrustPayUsedInProject,
    [project.numericId, paymentMethods],
  );

  useEffect(() => {
    if (isApplePaySupported) {
      setTimeout(() => {
        handleChange(PaymentMethod.ApplePay);
      }, 100);
    }
  }, [isApplePaySupported, hasHydrated]);

  const [field, meta, helpers] = useField('paymentMethod');

  const isErrorVisible = useMemo(() => {
    return !!(meta.error && meta.touched);
  }, [meta.error, meta.touched]);

  const handleChange = useCallback(
    (value: PaymentMethod) => {
      helpers.setValue(value);
    },
    [helpers],
  );

  const displayApplePayLogoInCardMethod = !isApplePaySupported && isAppleDevice;

  const iconResolver: Partial<Record<PaymentMethod, ReactNode>> = {
    [PaymentMethod.ApplePay]: <ApplePayLogo />,
    [PaymentMethod.Card]: displayApplePayLogoInCardMethod ? (
      <div className="grid-cols-[1fr_1fr] grid  gap-x-[8px] gap-y-[4px] items-center justify-center">
        <div className="w-full justify-center flex">
          <MastercardLogo className="w-auto h-[25px]" />
        </div>
        <VisaLogo className="w-auto h-[13px]" />
        <GooglePayLogo />
        <ApplePayLogo />
      </div>
    ) : (
      <div className="flex items-center gap-[4px]">
        <GooglePayLogo />
        <MastercardLogo className="w-auto h-[25px]" />
        <VisaLogo className="w-auto h-[13px]" />
      </div>
    ),
    [PaymentMethod.WireTransfer]: <WireTransferIcon />,
    [PaymentMethod.QrCode]: <QRCodeIcon className="mr-[4px] h-[24px] w-[24px] text-[#d9d9d9]" />,
  };

  const options = paymentMethods
    .filter((option) => option !== PaymentMethod.ApplePay || isApplePaySupported)
    .map((method) => ({
      children: iconResolver[method],
      value: method,
      label: t(`components.PaymentMethods.Enum.${method}`),
      noteWhenSelected: paymentMethodsWithNote.includes(method)
        ? t(`components.PaymentMethods.Note.${method}`)
        : undefined,
    }));

  return (
    <div style={{ height: 250 }} className={classNames({ 'has-error': isErrorVisible })}>
      {
        <label className="block mb-[8px] font-medium text-[16px] leading-[24px]">
          {t('components.DonationForm.paymentType')}
        </label>
      }
      {isErrorVisible && (
        <div className="text-warning font-medium mb-[8px]">
          <ErrorMessage name={'paymentMethod'} />
        </div>
      )}
      <div className="border-[2px] rounded-[8px] overflow-hidden border-[#D9D9D9]">
        {options.map((option, index) => (
          <ButtonRadio<PaymentMethod>
            className={classNames('border-b-[1px] border-neutral-light', {
              'border-none': index === options.length - 1, // Remove border for the last option
            })}
            key={`${String(option.value)}-${index}`}
            label={option.label}
            value={option.value}
            onChange={handleChange}
            isChecked={String(option.value) === String(field.value)}
            noteWhenSelected={option.noteWhenSelected}
          >
            {option.children}
          </ButtonRadio>
        ))}
      </div>
    </div>
  );
};

export default PaymentMethods;
